import * as React from "react";

import { graphql, Link } from "gatsby";
import { TopList } from "./fragments/top-list";
import { Layout } from "../components/layout/layout";
import { InfoPageHero } from "../components/layout/info-hero";
import { Text, Heading2 } from "../components/blog/parts";
import { SearchForm } from "../components/search/search-form";
import { getFormValuesForTopList } from "../services/getFormValuesForTopList";
import { PageIntro } from "../components/shared/page-intro";
import { OverviewItem } from "../components/shared/overview-item";
import { slugify } from "../services/slugify";
import {
    prepareNameMap,
    replacePlaceholdersWithLinks
} from "../services/replacePlaceholdersWithLinks";
import { GatsbyImage, getImage, IGatsbyImageData } from "gatsby-plugin-image";
import { Links, LinkType } from "../components/destination/links";
import { DestinationPageHero } from "../components/destination/hero";

interface TopListType {
    data: Query;
    pageContext: {
        slug: string;
        linkUrls: { [key: string]: string };
        topListLinks: LinkType[];
    };
}

const TopListPage = ({ data, pageContext }: TopListType) => {
    const topListJson = data.topListJson;
    const nameMap = prepareNameMap(topListJson);
    const replacePlaceholdersWithLinksFc = (text: string, slug?: string) => {
        return replacePlaceholdersWithLinks({
            text,
            slugMap: pageContext.linkUrls,
            nameMap,
            topListData: topListJson,
            slug
        });
    };
    const persona1 = topListJson.additionalData.personas[0];
    const persona2 = topListJson.additionalData.personas[1];
    return (
        <Layout
            path={"/" + pageContext.slug}
            title={topListJson.title}
            description={topListJson.description}
        >
            <DestinationPageHero
                className="lg:hidden"
                small
                doNotShowBreadcrumbs
                doNotShowMonthPicker
                bgImage={getImage(
                    topListJson.additionalData.resorts[0].heroImgFull
                        .childImageSharp
                )}
                header={topListJson.title}
            />
            <DestinationPageHero
                className="lgmax:hidden"
                small
                doNotShowBreadcrumbs
                doNotShowMonthPicker
                bgImage={getImage(
                    topListJson.additionalData.resorts[0].heroImg
                        .childImageSharp
                )}
                header={topListJson.title}
            />
            <div className="w-page-overview m-auto mb-6 mt-12">
                <PageIntro
                    centered
                    overviewTitle={
                        <div className="text-2xl font-bold">
                            TOP Destinations
                        </div>
                    }
                    overviewNode={
                        <ul>
                            {topListJson.destinations.map(
                                (destination, idx) => (
                                    <OverviewItem
                                        className="font-semibold"
                                        key={destination.headline}
                                        name={`${idx + 1}. ${
                                            topListJson.additionalData.resorts[
                                                idx
                                            ].name
                                        }`}
                                        id={`#${slugify(destination.headline)}`}
                                    />
                                )
                            )}
                        </ul>
                    }
                    introContent={replacePlaceholdersWithLinksFc(
                        topListJson.introduction?.content
                    )}
                />
            </div>
            <div className="w-text-article m-auto mt-12 mb-8">
                <Heading2 id="CTA">{topListJson.CTA?.headline}</Heading2>
            </div>
            <SearchForm defaultValues={getFormValuesForTopList(topListJson)} />

            <div className="w-text-article m-auto mt-8 mb-12">
                <Text>{topListJson.CTA?.content}</Text>
                <div className="h-6" />
                <Heading2 id="factorsToLookFor">
                    {topListJson.factorsToLookFor?.headline}
                </Heading2>
                <Text>
                    {replacePlaceholdersWithLinksFc(
                        topListJson.factorsToLookFor?.content
                    )}
                </Text>
                <hr className="m-auto mt-16 mb-12 w-full" />

                {topListJson.destinations.map((destination, idx) => {
                    const resortData = topListJson.additionalData.resorts[idx];
                    const image = getImage(
                        resortData.mainImg.childImageSharp.gatsbyImageData
                    );
                    return getDestinationMarkup({
                        destination,
                        image,
                        useActivitiesText:
                            topListJson.additionalData.activitiesText,
                        replacePlaceholdersWithLinksFc,
                        slug: resortData.slug
                    });
                })}
                <hr className="m-auto mt-16 mb-12 w-full" />

                <Heading2 id="otherNotableDestinations">
                    {topListJson.otherNotableDestinations?.headline}
                </Heading2>

                <Text>
                    {replacePlaceholdersWithLinksFc(
                        topListJson.otherNotableDestinations?.content
                    )}
                </Text>
                <hr className="m-auto mt-16 mb-12 w-full" />
                {topListJson.regions.map((destination, idx) => {
                    const regionData = topListJson.additionalData.regions[idx];
                    const image = getImage(
                        regionData.mainImg.childImageSharp.gatsbyImageData
                    );
                    return getDestinationMarkup({
                        destination,
                        image,
                        useActivitiesText:
                            topListJson.additionalData.activitiesText,
                        replacePlaceholdersWithLinksFc,
                        slug: regionData.slug
                    });
                })}

                <hr className="m-auto mt-16 mb-12 w-full" />
                <Heading2 id="opinions">Opinions</Heading2>
                <Text>
                    <Link to={`/our-team/${persona1.slug}`}>
                        <ProfileImage
                            image={getImage(
                                persona1.profileImg.childImageSharp
                                    .gatsbyImageData
                            )}
                        />
                        <b>
                            {persona1.name} ({persona1.age}):{" "}
                        </b>
                    </Link>
                    <i>
                        "
                        {replacePlaceholdersWithLinksFc(
                            topListJson.opinions?.persona1?.content
                        )}
                        "
                    </i>
                </Text>
                <div className="h-6 lg:h-10" />
                <Text>
                    <Link to={`/our-team/${persona2.slug}`}>
                        <ProfileImage
                            image={getImage(
                                persona2.profileImg.childImageSharp
                                    .gatsbyImageData
                            )}
                        />
                        <b>
                            {persona2.name} ({persona2.age}):{" "}
                        </b>
                    </Link>
                    <i>
                        "
                        {replacePlaceholdersWithLinksFc(
                            topListJson.opinions?.persona2?.content
                        )}
                        "
                    </i>
                </Text>
            </div>
            <Links links={pageContext.topListLinks} title="Similar TOP lists" />
        </Layout>
    );
};

const ProfileImage = ({ image }: { image: IGatsbyImageData }) => {
    return (
        <GatsbyImage
            className="float-left mx-auto mb-4 mr-8 w-[80px] rounded-md"
            image={image}
            alt=""
        />
    );
};

const getDestinationMarkup = ({
    destination,
    image,
    slug,
    useActivitiesText,
    replacePlaceholdersWithLinksFc
}: {
    destination: {
        headline: string;
        generalInfo: { content: string };
        whyItsPerfect: { content: string };
        activities: { content: string };
    };
    slug: string;
    image: IGatsbyImageData;
    useActivitiesText: boolean;
    replacePlaceholdersWithLinksFc: (
        text: string,
        slug?: string
    ) => React.ReactNode;
}) => {
    return (
        <>
            <Heading2 id={`${slugify(destination.headline)}`}>
                {destination.headline}
            </Heading2>
            <div className="mb-4 w-full flex-row items-center tsmax:flex">
                <GatsbyImage
                    className="mx-auto mb-4 w-full rounded-md xs:w-[280px] xm:float-left ts:mr-8 ts:w-[240px] sm:w-[320px]"
                    image={image}
                    alt=""
                />
            </div>
            <Text>
                {replacePlaceholdersWithLinksFc(
                    destination.generalInfo?.content,
                    slug
                )}
            </Text>
            <Text>
                {replacePlaceholdersWithLinksFc(
                    destination.whyItsPerfect?.content,
                    slug
                )}
            </Text>
            {useActivitiesText && (
                <Text>
                    {replacePlaceholdersWithLinksFc(
                        destination.activities?.content,
                        slug
                    )}
                </Text>
            )}
            <div className="h-4" />
        </>
    );
};

interface Query {
    topListJson: TopList;
}

export const query = graphql`
    query TopListJson($slug: String) {
        topListJson(slug: { eq: $slug }) {
            ...TopList
        }
    }
`;

export default TopListPage;
